import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@akuna/ui";
import { Input } from "@akuna/ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Alert, AlertDescription } from "@akuna/ui";
import { useProfile, useUpdateProfile } from "@/hooks/useProfile";
import { profileSchema } from "@/lib/validations/profile";
import type { ProfileInput } from "@/lib/validations/profile";
import { useStates } from "@/hooks/useLookups";
import { useVetPrescriptionCounter } from "@/hooks/useVetPrescriptionCounter";

interface ProfileDataModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  needsCRMV: boolean;
  needsMAPA: boolean;
  needsAddress: boolean;
  onComplete: () => void;
  contextLabel?: string;
}

export function ProfileDataModal({
  open,
  onOpenChange,
  needsCRMV,
  needsMAPA,
  needsAddress,
  onComplete,
  contextLabel,
}: ProfileDataModalProps) {
  const { t } = useTranslation("prescriptions");
  const resolvedContextLabel = contextLabel ?? t("profileModal.defaultContext");
  const { data: profile } = useProfile();
  const updateProfile = useUpdateProfile();
  const { data: states } = useStates();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const cancelledRef = useRef(false);

  const { counter, updateCounter } = useVetPrescriptionCounter(profile?.id, open);

  const [counterValue, setCounterValue] = useState<string>("");

  // Sync counter value when data loads
  useEffect(() => {
    if (counter && open) {
      setCounterValue(String(counter.current_value));
    }
  }, [counter, open]);

  const form = useForm<ProfileInput>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      email: "",
      full_name: "",
      phone: "",
      cpf: "",
      crmv_state: "",
      crmv_number: null,
      mapa_number: "",
      address: "",
    },
  });

  // Atualizar valores quando profile mudar ou modal abrir
  useEffect(() => {
    if (open) {
      cancelledRef.current = false;
    }
    if (profile && open) {
      form.reset({
        email: profile.email || "",
        full_name: profile.full_name || "",
        phone: profile.phone || "",
        cpf: profile.cpf || "",
        crmv_state: profile.crmv_state || "",
        crmv_number: profile.crmv_number || null,
        mapa_number: profile.mapa_number || "",
        address: profile.address || "",
      });
    }
  }, [profile, open, form]);

  const onSubmit = async (data: ProfileInput) => {
    // Validar que os campos obrigatórios estão preenchidos
    if (needsCRMV) {
      if (!data.crmv_state || data.crmv_state.trim() === "") {
        form.setError("crmv_state", { message: t("profileModal.errors.crmvStateRequired", { context: resolvedContextLabel }) });
        return;
      }
      if (!data.crmv_number || data.crmv_number <= 0) {
        form.setError("crmv_number", { message: t("profileModal.errors.crmvNumberRequired", { context: resolvedContextLabel }) });
        return;
      }
    }

    if (needsMAPA && (!data.mapa_number || data.mapa_number.trim() === "")) {
      form.setError("mapa_number", { message: t("profileModal.errors.mapaRequired", { context: resolvedContextLabel }) });
      return;
    }

    if (needsAddress && (!data.address || data.address.trim() === "")) {
      form.setError("address", { message: t("profileModal.errors.addressRequired", { context: resolvedContextLabel }) });
      return;
    }

    setIsSubmitting(true);
    try {
      await updateProfile.mutateAsync(data);
      // Aguardar um pouco para garantir que o toast apareça
      await new Promise((resolve) => setTimeout(resolve, 100));
      setIsSubmitting(false);
      if (cancelledRef.current) return;
      // Chamar onComplete - ele vai fechar o modal e criar a receita
      onComplete();
    } catch (error) {
      // Error já tratado no hook
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      cancelledRef.current = true;
      onOpenChange(false);
      return;
    }
    onOpenChange(newOpen);
  };

  const missingFields: string[] = [];
  if (needsCRMV) missingFields.push("CRMV");
  if (needsMAPA) missingFields.push("MAPA");
  if (needsAddress) missingFields.push(t("profileModal.address"));

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md" overlayClassName="profile-data-modal-overlay">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-amber-500" />
            {t("profileModal.title")}
          </DialogTitle>
          <DialogDescription>
            {t("profileModal.description", { context: resolvedContextLabel })}
          </DialogDescription>
        </DialogHeader>

        <Alert className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {t("profileModal.requiredFields", { fields: missingFields.join(t("profileModal.fieldsSeparator")) })}
          </AlertDescription>
        </Alert>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {needsCRMV && (
              <>
                <FormField
                  control={form.control}
                  name="crmv_state"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("profileModal.crmvState")}</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value || ""}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={t("profileModal.selectState")} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {states?.map((state) => (
                            <SelectItem key={state.id} value={state.code}>
                              {state.name} ({state.code})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                      <p className="text-xs text-muted-foreground">
                        {t("profileModal.crmvStateHelp")}
                      </p>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="crmv_number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("profileModal.crmvNumber")}</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder={t("profileModal.numberExample")}
                          {...field}
                          value={field.value || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            field.onChange(value === "" ? null : parseInt(value, 10));
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                      <p className="text-xs text-muted-foreground">
                        {t("profileModal.crmvNumberHelp")}
                      </p>
                    </FormItem>
                  )}
                />
              </>
            )}

            {needsMAPA && (
              <FormField
                control={form.control}
                name="mapa_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("profileModal.mapaNumber")}</FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        placeholder={t("profileModal.numberExample")}
                        {...field}
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                    <p className="text-xs text-muted-foreground">
                      {t("profileModal.mapaNumberHelp")}
                    </p>
                  </FormItem>
                )}
              />
            )}

            {needsAddress && (
              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("profileModal.address")}</FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        placeholder={t("profileModal.addressPlaceholder")}
                        maxLength={300}
                        {...field}
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                    <p className="text-xs text-muted-foreground">
                      {t("profileModal.addressHelp")}
                    </p>
                  </FormItem>
                )}
              />
            )}

            <div className="space-y-2 rounded-lg border border-border/70 p-3">
              <FormLabel>{t("profileModal.counterLabel")}</FormLabel>
              <div className="flex gap-2">
                <Input
                  type="number"
                  min={0}
                  value={counterValue}
                  onChange={(e) => setCounterValue(e.target.value)}
                  placeholder="0"
                />
                <Button
                  type="button"
                  variant="outline"
                  disabled={updateCounter.isPending}
                  onClick={() => {
                    const parsed = parseInt(counterValue, 10);
                    if (!Number.isFinite(parsed) || parsed < 0) return;
                    updateCounter.mutate(parsed);
                  }}
                >
                  {updateCounter.isPending
                    ? t("profileModal.saving")
                    : t("profileModal.updateCounter")}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                {t("profileModal.counterHelp")}
              </p>
            </div>

            <DialogFooter>
              <Button
                type="submit"
                disabled={isSubmitting || updateProfile.isPending}
                className="w-full"
              >
                {isSubmitting || updateProfile.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("profileModal.saving")}
                  </>
                ) : (
                  t("profileModal.save")
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
